import { DynamicStructure, EMPTY_VALUE, FieldName, FieldValues, isEmpty } from "../../../Schema";
import { CategoryMapping } from "../../common/external-sources/CategoryMappings";
import { ErrorMessage } from "../../../validation/ValidationManager";

/**
 * The one master-data rule no single field can state: a manufacturer, model and variant are
 * only valid *for their category*.
 *
 * Those four declare their options on `grid.filterValues` rather than on `data`, because the
 * form drives them together through the MasterDataPicker and putting them on `data` would
 * change coercion and rendering for a filter's sake. That leaves no field knowing which
 * combinations exist — and it is the combination, not any of the four values, that this
 * checks. So it is a relation, and it lives on `manufacturer`: the one of the four that only
 * an equipment has, and the field whose value is most often the wrong half of a valid-looking
 * pair.
 *
 * Replaces the validateCategory → validateManufacturerMasterData → validateModelMasterData →
 * validateVariantMasterData chain the CSV import carried, which was the last thing in that
 * lambda the schema could not answer for.
 */

/**
 * Whether a mapping row matches every part of the tuple the row supplied.
 *
 * A part the row left empty is not compared: an equipment need not carry a variant, and
 * `required` is what objects to a missing category. Only the parts that are present have to
 * agree with one another.
 *
 * @param mapping - One row of the category-mapping master data.
 * @param values - The row's values.
 * @returns Whether this mapping is consistent with what the row said.
 */
const matchesSuppliedParts = (mapping: CategoryMapping, values: FieldValues): boolean =>
  (["equipmentCategory", "manufacturer", "equipmentModel", "variant"] as const)
    .every((part) => isEmpty(values[part]) || mapping[part] === values[part]);

/**
 * Builds the rule, closing over the mappings captured when the schema was built.
 *
 * An empty list means the master data could not be read, not that every tuple is wrong.
 * Staying quiet then is deliberate and matches how an enum with no resolvable options is
 * treated: refusing a whole file because a table was briefly unreachable is worse than the
 * invalid rows it would have caught, and unlike an enum there is nothing to coerce away, so
 * no value is lost by not checking.
 *
 * @param categoryMappings - The valid combinations, from the master data.
 * @returns The rule to put on `manufacturer`'s `validation.custom`.
 */
export const validCategoryTuple = (
  categoryMappings: CategoryMapping[]
): DynamicStructure<ErrorMessage, FieldName[]> => ({
  getter: (values: FieldValues): ErrorMessage => {
    if (!categoryMappings.length) {
      return EMPTY_VALUE;
    }
    // Nothing to relate until the row names at least a category and a manufacturer; whichever
    // of them is missing is reported by its own `required` rule.
    if (isEmpty(values.equipmentCategory) || isEmpty(values.manufacturer)) {
      return EMPTY_VALUE;
    }
    if (categoryMappings.some((mapping) => matchesSuppliedParts(mapping, values))) {
      return EMPTY_VALUE;
    }
    const tuple = [
      values.equipmentCategory,
      values.manufacturer,
      values.equipmentModel,
      values.variant,
    ]
      .filter((part) => !isEmpty(part))
      .join(" / ");
    return `${tuple} is not a valid category, manufacturer, model and variant combination`;
  },
  fields: ["equipmentCategory", "manufacturer", "equipmentModel", "variant"],
});
